import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

interface DocumentationViewerProps {
  isOpen: boolean;
  onClose: () => void;
  documentPath: string;
  onDocumentChange?: (newPath: string) => void;
}

interface TOCItem {
  level: number;
  text: string;
  id: string;
}

const DocumentationViewer: React.FC<DocumentationViewerProps> = ({
  isOpen,
  onClose,
  documentPath,
  onDocumentChange,
}) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [tocItems, setTocItems] = useState<TOCItem[]>([]);
  const [showTOC, setShowTOC] = useState<boolean>(true);
  const [documentHistory, setDocumentHistory] = useState<string[]>([]);

  const loadDocument = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(documentPath);
      if (!response.ok) {
        throw new Error('Document not found');
      }
      const text = await response.text();
      setContent(text);

      // Extract table of contents from markdown headers
      extractTOC(text);

      // Scroll to top when new document loads
      const contentArea = document.querySelector('.documentation-content');
      if (contentArea) {
        contentArea.scrollTop = 0;
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to load documentation. Please try again.');
      setLoading(false);
      console.error('Error loading document:', err);
    }
  }, [documentPath]);

  useEffect(() => {
    if (isOpen && documentPath) {
      loadDocument();

      // Add to history if it's a new document
      setDocumentHistory((prev) => {
        if (prev[prev.length - 1] !== documentPath) {
          return [...prev, documentPath];
        }
        return prev;
      });
    } else if (!isOpen) {
      // Reset history when modal is closed
      setDocumentHistory([]);
    }
  }, [isOpen, documentPath, loadDocument]);

  const handleGoBack = () => {
    if (documentHistory.length > 1) {
      // Remove current document and go to previous
      const newHistory = [...documentHistory];
      newHistory.pop();
      const previousDoc = newHistory[newHistory.length - 1];
      setDocumentHistory(newHistory);
      onDocumentChange?.(previousDoc);
    }
  };

  const extractTOC = (markdown: string) => {
    const headerRegex = /^(#{1,6})\s+(.+)$/gm;
    const items: TOCItem[] = [];
    let match;

    while ((match = headerRegex.exec(markdown)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();

      // Skip h1 headers and headers with special markers
      if (level === 1 || text.includes('---')) continue;

      // Create ID from header text
      const id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');

      items.push({ level, text, id });
    }

    setTocItems(items);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(`toc-${id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getDocumentTitle = () => {
    const pathParts = documentPath.split('/');
    const filename = pathParts[pathParts.length - 1];
    return filename
      .replace('.md', '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col pointer-events-auto animate-slide-up">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-primary-50 to-white">
            <div className="flex items-center space-x-3">
              {documentHistory.length > 1 && (
                <button
                  onClick={handleGoBack}
                  className="p-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Go back to previous document"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                    />
                  </svg>
                </button>
              )}
              <svg
                className="w-6 h-6 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                />
              </svg>
              <h2 className="text-xl font-semibold text-neutral-900">
                {getDocumentTitle()}
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowTOC(!showTOC)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                  />
                </svg>
                {showTOC ? 'Hide' : 'Show'} Contents
              </button>
              <button
                onClick={onClose}
                className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex flex-1 overflow-hidden">
            {/* Table of Contents Sidebar */}
            {showTOC && tocItems.length > 0 && (
              <div className="w-64 border-r border-neutral-200 bg-neutral-50 overflow-y-auto">
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-neutral-900 mb-3 uppercase tracking-wide">
                    Table of Contents
                  </h3>
                  <nav className="space-y-1">
                    {tocItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => scrollToSection(item.id)}
                        className={`block w-full text-left px-2 py-1.5 text-sm text-neutral-700 hover:text-primary-600 hover:bg-white rounded transition-colors ${
                          item.level === 2 ? 'font-medium' : ''
                        }`}
                        style={{ paddingLeft: `${(item.level - 2) * 12 + 8}px` }}
                      >
                        {item.text}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            )}

            {/* Document Content */}
            <div className="flex-1 overflow-y-auto documentation-content">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent"></div>
                    <p className="mt-4 text-neutral-600">Loading documentation...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center max-w-md">
                    <svg
                      className="w-16 h-16 text-red-500 mx-auto mb-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                      />
                    </svg>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                      Failed to Load Document
                    </h3>
                    <p className="text-neutral-600 mb-4">{error}</p>
                    <button
                      onClick={loadDocument}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-8 py-6">
                  <article className="prose prose-neutral max-w-none prose-headings:scroll-mt-20">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                      components={{
                        // Add IDs to headers for TOC navigation
                        h1: ({ node, children, ...props }) => {
                          const text = children?.toString() || '';
                          const id = text
                            .toLowerCase()
                            .replace(/[^\w\s-]/g, '')
                            .replace(/\s+/g, '-');
                          return <h1 id={`toc-${id}`} {...props}>{children}</h1>;
                        },
                        h2: ({ node, children, ...props }) => {
                          const text = children?.toString() || '';
                          const id = text
                            .toLowerCase()
                            .replace(/[^\w\s-]/g, '')
                            .replace(/\s+/g, '-');
                          return <h2 id={`toc-${id}`} {...props}>{children}</h2>;
                        },
                        h3: ({ node, children, ...props }) => {
                          const text = children?.toString() || '';
                          const id = text
                            .toLowerCase()
                            .replace(/[^\w\s-]/g, '')
                            .replace(/\s+/g, '-');
                          return <h3 id={`toc-${id}`} {...props}>{children}</h3>;
                        },
                        h4: ({ node, children, ...props }) => {
                          const text = children?.toString() || '';
                          const id = text
                            .toLowerCase()
                            .replace(/[^\w\s-]/g, '')
                            .replace(/\s+/g, '-');
                          return <h4 id={`toc-${id}`} {...props}>{children}</h4>;
                        },
                        // Style code blocks
                        code: ({ node, className, children, ...props }) => {
                          const isInline = !className;
                          return isInline ? (
                            <code
                              className="px-1.5 py-0.5 bg-neutral-100 text-primary-700 rounded text-sm font-mono"
                              {...props}
                            >
                              {children}
                            </code>
                          ) : (
                            <code
                              className={`block bg-neutral-900 text-neutral-100 p-4 rounded-lg overflow-x-auto ${className || ''}`}
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        },
                        // Style tables
                        table: ({ node, ...props }) => (
                          <div className="overflow-x-auto my-6">
                            <table
                              className="min-w-full divide-y divide-neutral-200 border border-neutral-200 rounded-lg"
                              {...props}
                            />
                          </div>
                        ),
                        // Style links
                        a: ({ node, children, href, ...props }) => {
                          // Check if this is an internal documentation link
                          const isInternalDoc = href && (
                            href.endsWith('.md') ||
                            href.includes('.md#')
                          );

                          if (isInternalDoc) {
                            // Internal documentation link - open in viewer
                            return (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  const newPath = href.startsWith('/')
                                    ? href
                                    : `/docs/${href}`;
                                  onDocumentChange?.(newPath);
                                }}
                                className="text-primary-600 hover:text-primary-700 font-medium underline decoration-primary-300 hover:decoration-primary-500 transition-colors cursor-pointer"
                              >
                                {children}
                              </button>
                            );
                          }

                          // External link - open in new tab
                          return (
                            <a
                              href={href}
                              className="text-primary-600 hover:text-primary-700 font-medium underline decoration-primary-300 hover:decoration-primary-500 transition-colors"
                              target="_blank"
                              rel="noopener noreferrer"
                              {...props}
                            >
                              {children}
                            </a>
                          );
                        },
                        // Style blockquotes
                        blockquote: ({ node, ...props }) => (
                          <blockquote
                            className="border-l-4 border-primary-500 bg-primary-50 pl-4 py-2 my-4 italic"
                            {...props}
                          />
                        ),
                      }}
                    >
                      {content}
                    </ReactMarkdown>
                  </article>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-neutral-200 px-6 py-3 bg-neutral-50">
            <div className="flex items-center justify-between text-sm text-neutral-600">
              <div className="flex items-center space-x-4">
                <span className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-1 text-primary-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                    />
                  </svg>
                  PDF Form Builder Documentation
                </span>
                <a
                  href="mailto:support@cavuinnovations.com"
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Need Help?
                </a>
              </div>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentationViewer;
